import { RangeControl } from "./controls";
import { Icon } from "./Icon";

export interface RestorationState {
  status: "idle" | "processing" | "ready" | "error";
  strength: number;
  compare: number;
  provider?: "webgpu" | "wasm";
  phase?: "loading" | "processing";
  completed?: number;
  total?: number;
  error?: string;
}

export const INITIAL_RESTORATION: RestorationState = {
  status: "idle",
  strength: 80,
  compare: 0.5,
};

function providerLabel(provider: RestorationState["provider"]): string {
  if (provider === "webgpu") return " (GPU)";
  if (provider === "wasm") return " (CPU)";
  return "";
}

export function restorationLabel(restoration: RestorationState): string {
  switch (restoration.status) {
    case "processing":
      if (restoration.phase === "processing" && restoration.total !== undefined) {
        return `復元中 ${restoration.completed ?? 0} / ${restoration.total}${providerLabel(
          restoration.provider,
        )}`;
      }
      return `復元モデルを読み込み中…${providerLabel(restoration.provider)}`;
    case "error":
      return "復元できませんでした";
    case "ready":
      return `復元結果を確認してください${providerLabel(restoration.provider)}`;
    default:
      return "キャンバス上で復元したい範囲をドラッグ";
  }
}

interface RestorePanelProps {
  restoration: RestorationState;
  onChange: (patch: Partial<RestorationState>) => void;
  onApply: () => void;
  onRetry: () => void;
  onCancel: () => void;
}

export function RestorePanel({
  restoration,
  onChange,
  onApply,
  onRetry,
  onCancel,
}: RestorePanelProps) {
  const ready = restoration.status === "ready";
  return (
    <aside className="side-panel" aria-label="AI復元">
      <h2 className="panel-title">
        <Icon name="sparkles" size={18} />
        AI復元
      </h2>
      <p className="panel-text">
        ぼやけた部分や小さな部分を、端末内のAIで高精細に描き直します。結果はAIの推測であり、元の画像を再現するものではありません。
      </p>
      <div className="model-status" role="status">
        <span className={`status-dot status-${restoration.status}`} aria-hidden="true" />
        <span>{restorationLabel(restoration)}</span>
      </div>
      {restoration.error && (
        <p className="inline-error" role="alert">
          {restoration.error}
        </p>
      )}
      <div className="panel-section">
        <RangeControl
          label="比較位置"
          value={Math.round(restoration.compare * 100)}
          min={0}
          max={100}
          step={1}
          unit="%"
          disabled={!ready}
          onChange={(value) => onChange({ compare: value / 100 })}
        />
        <RangeControl
          label="復元の強さ"
          value={restoration.strength}
          min={0}
          max={100}
          step={1}
          unit="%"
          disabled={!ready}
          onChange={(strength) => onChange({ strength })}
        />
      </div>
      <div className="panel-actions">
        <button className="primary-button" type="button" disabled={!ready} onClick={onApply}>
          <Icon name="check" size={16} />
          結果を適用
        </button>
        <button
          className="secondary-button"
          type="button"
          disabled={restoration.status === "idle" || restoration.status === "processing"}
          onClick={onRetry}
        >
          <Icon name="refresh" size={16} />
          もう一度
        </button>
        <button
          className="quiet-button"
          type="button"
          disabled={restoration.status === "idle"}
          onClick={onCancel}
        >
          キャンセル
        </button>
      </div>
    </aside>
  );
}
